// src/components/Trading/LiveAlertsTabEnhanced.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Grid,
  Chip,
  IconButton,
  Badge,
  Alert,
  Button,
  TextField,
  LinearProgress,
  CircularProgress,
  Collapse,
  Fade,
  Zoom,
  Divider,
  Avatar,
  AvatarGroup,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  alpha,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import {
  Notifications as BellIcon,
  NotificationsActive as ActiveBellIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  AttachMoney as MoneyIcon,
  ShowChart as ChartIcon,
  Bolt as BoltIcon,
  Circle as DotIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  SignalCellularAlt as SignalIcon,
  AutoAwesome as SparkleIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import NotificationSystem from './NotificationSystem';
import StrategyProgressTracker from './StrategyProgressTracker';

const LiveAlertsTabEnhanced = ({ apiBaseUrl = window.location.origin }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
  
  const [alerts, setAlerts] = useState([]);
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    entries: 0,
    exits: 0,
    early: 0,
    activeStrategies: new Set(),
  });
  const [realtimeAlerts, setRealtimeAlerts] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);
  
  const wsRef = useRef(null);
  const notificationIdRef = useRef(0);
  const alertIdRef = useRef(0);

  // Add notification (trading signals only)
  const addNotification = useCallback((type, title, message, data = {}) => {
    const id = `notif-${++notificationIdRef.current}-${Date.now()}`;
    const notification = {
      id,
      type,
      title,
      message,
      timestamp: new Date().toISOString(),
      ...data
    };
    setNotifications(prev => [...prev, notification]);
    return id;
  }, []);

  // Remove notification
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Load historical alerts from database on mount
  useEffect(() => {
    loadHistoricalAlerts();
  }, []);

  // Connect to WebSocket
  useEffect(() => {
    connectWebSocket();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const loadHistoricalAlerts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiBaseUrl}/strategies/api/alerts/live/?limit=20`);
      const data = await response.json();
      
      if (data.success && data.alerts && data.alerts.length > 0) {
        const historicalAlerts = data.alerts.map((alert) => ({
          id: `hist-${alert.id}`,
          signal_type: alert.signal_type,
          strategy_id: alert.strategy_id,
          message: alert.message,
          price: alert.price,
          timeframe: alert.timeframe,
          signal: alert.signal_type,
          timestamp: alert.timestamp,
          raw: alert.alert_data,
        }));
        
        setAlerts(historicalAlerts);
        updateStatsFromAlerts(historicalAlerts);
        setHasLoadedHistory(true);
      } else {
        setHasLoadedHistory(true);
      }
    } catch (error) {
      console.error('Failed to load historical alerts:', error);
      setHasLoadedHistory(true);
    } finally {
      setLoading(false);
    }
  };

  const updateStatsFromAlerts = (alertList) => {
    setStats({
      total: alertList.length,
      entries: alertList.filter(a => a.signal_type === 'entry').length,
      exits: alertList.filter(a => a.signal_type === 'exit').length,
      early: alertList.filter(a => a.signal_type === 'early').length,
      activeStrategies: new Set(alertList.map(a => a.strategy_id).filter(Boolean)),
    });
  };

  const connectWebSocket = () => {
    try {
      const wsUrl = apiBaseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
      const ws = new WebSocket(`${wsUrl}/ws/strategy-alerts/`);
      
      ws.onopen = () => {
        setWsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onerror = () => {
        // Silent error for traders
      };

      ws.onclose = () => {
        setWsConnected(false);
        setTimeout(connectWebSocket, 5000);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  };

  const handleWebSocketMessage = (data) => {
    const alertId = `alert-${++alertIdRef.current}-${Date.now()}`;
    const alert = {
      id: alertId,
      signal_type: data.signal_type,
      strategy_id: data.strategy_id,
      message: data.message,
      price: data.price,
      timeframe: data.timeframe,
      signal: data.signal,
      timestamp: data.timestamp || new Date().toISOString(),
      raw: data,
    };

    setAlerts(prev => [alert, ...prev].slice(0, 100));
    setRealtimeAlerts(prev => [alert, ...prev].slice(0, 5));
    updateStats(data);

    // Trading signals only - no system messages
    if (data.signal_type === 'entry') {
      addNotification('success', 'Entry Signal', data.message, {
        strategy: data.strategy_id,
        price: data.price,
        signal: data.signal,
      });
    } else if (data.signal_type === 'exit') {
      addNotification('info', 'Exit Signal', data.message, {
        strategy: data.strategy_id,
        price: data.price,
      });
    }
  };

  const updateStats = (data) => {
    setStats(prev => {
      const newStats = { ...prev };
      newStats.total = prev.total + 1;
      
      if (data.signal_type === 'entry') newStats.entries++;
      else if (data.signal_type === 'exit') newStats.exits++;
      else if (data.signal_type === 'early') newStats.early++;
      
      if (data.strategy_id) {
        newStats.activeStrategies.add(data.strategy_id);
      }
      
      return newStats;
    });
  };

  // Filter alerts
  useEffect(() => {
    let filtered = [...alerts];
    
    if (filter !== 'all') {
      filtered = filtered.filter(alert => alert.signal_type === filter);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(alert => 
        alert.message?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        alert.strategy_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        alert.signal?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredAlerts(filtered);
  }, [alerts, filter, searchQuery]);

  const getAlertIcon = (type) => {
    switch(type) {
      case 'entry':
        return <CheckIcon sx={{ color: '#00ff88' }} />;
      case 'exit':
        return <ErrorIcon sx={{ color: '#ff4757' }} />;
      case 'early':
        return <WarningIcon sx={{ color: '#ffa502' }} />;
      case 'broadcast':
        return <SignalIcon sx={{ color: '#00d4ff' }} />;
      default:
        return <DotIcon sx={{ color: '#666' }} />;
    }
  };

  const getAlertColor = (type) => {
    switch(type) {
      case 'entry': return '#00ff88';
      case 'exit': return '#ff4757';
      case 'early': return '#ffa502';
      case 'broadcast': return '#00d4ff';
      default: return '#666';
    }
  };

  const clearAlerts = () => {
    setAlerts([]);
    setRealtimeAlerts([]);
    setStats({
      total: 0,
      entries: 0,
      exits: 0,
      early: 0,
      activeStrategies: new Set(),
    });
  };

  const sendTestSignal = async () => {
    try {
      await fetch(`${apiBaseUrl}/api/signal/broadcast/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signal: 'EARLY_SIGNAL',
          timeframe: '1s',
          price: 50000 + Math.random() * 1000,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (error) {
      console.error('Failed to send test signal:', error);
    }
  };

  const responsivePadding = isLargeScreen ? 4 : isTablet ? 2.5 : 2;
  const responsiveSpacing = isLargeScreen ? 3 : isTablet ? 2.5 : 2;
  const headerPadding = isLargeScreen ? 3 : isTablet ? 2.5 : 2;
  const cardHeight = isLargeScreen ? '700px' : isTablet ? '600px' : 'auto';

  return (
    <Box sx={{ p: responsivePadding, position: 'relative', minHeight: '100vh' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: headerPadding,
          mb: responsiveSpacing,
          background: 'linear-gradient(135deg, rgba(15, 20, 40, 0.95) 0%, rgba(20, 25, 45, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: alpha('#00d4ff', 0.2),
          borderRadius: isTablet ? 2.5 : 3,
        }}
      >
        <Grid container alignItems="center" spacing={responsiveSpacing}>
          <Grid item xs={12} md={6}>
            <Stack direction="row" alignItems="center" spacing={isTablet ? 1.5 : 2}>
              <Badge
                badgeContent={stats.total}
                color="error"
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    bgcolor: '#ff4757',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  }
                }}
              >
                <Box
                  sx={{
                    width: isTablet ? 48 : 56,
                    height: isTablet ? 48 : 56,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #ff4757 0%, #ff6b6b 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(255, 71, 87, 0.3)',
                    animation: stats.total > 0 ? 'bell-ring 1s' : 'none',
                  }}
                >
                  {stats.total > 0 ? (
                    <ActiveBellIcon sx={{ fontSize: isTablet ? 24 : 28, color: '#fff' }} />
                  ) : (
                    <BellIcon sx={{ fontSize: isTablet ? 24 : 28, color: '#fff' }} />
                  )}
                </Box>
              </Badge>
              <Box>
                <Typography variant={isTablet ? 'h6' : 'h5'} sx={{ color: '#fff', fontWeight: 700 }}>
                  Live Alerts
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#00d4ff', 0.7), display: { xs: 'none', sm: 'block' } }}>
                  Real-time trading signals and notifications
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Chip
                icon={wsConnected ? <DotIcon /> : <DotIcon />}
                label={wsConnected ? 'Live' : 'Offline'}
                sx={{
                  bgcolor: wsConnected ? alpha('#00ff88', 0.2) : alpha('#ff4757', 0.2),
                  color: wsConnected ? '#00ff88' : '#ff4757',
                  borderColor: wsConnected ? '#00ff88' : '#ff4757',
                  '& .MuiChip-icon': {
                    color: wsConnected ? '#00ff88' : '#ff4757',
                    animation: wsConnected ? 'pulse 2s infinite' : 'none',
                  }
                }}
              />
              
              <Button
                variant="outlined"
                size={isTablet ? 'small' : 'medium'}
                startIcon={<SendIcon />}
                onClick={sendTestSignal}
                sx={{
                  borderColor: alpha('#00d4ff', 0.5),
                  color: '#00d4ff',
                  '&:hover': {
                    borderColor: '#00d4ff',
                    bgcolor: alpha('#00d4ff', 0.1),
                  }
                }}
              >
                {isTablet ? 'Test' : 'Test Signal'}
              </Button>

              <Button
                variant="outlined"
                size={isTablet ? 'small' : 'medium'}
                startIcon={<RefreshIcon />}
                onClick={loadHistoricalAlerts}
                sx={{
                  borderColor: alpha('#00d4ff', 0.5),
                  color: '#00d4ff',
                  '&:hover': {
                    borderColor: '#00d4ff',
                    bgcolor: alpha('#00d4ff', 0.1),
                  }
                }}
              >
                {isTablet ? 'Refresh' : 'Refresh History'}
              </Button>
              
              <IconButton
                onClick={clearAlerts}
                sx={{
                  color: '#ff4757',
                  '&:hover': { bgcolor: alpha('#ff4757', 0.1) }
                }}
              >
                <ClearIcon />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Real-time Ticker */}
      <Paper
        elevation={0}
        sx={{
          p: isTablet ? 1.5 : 2,
          mb: responsiveSpacing,
          background: alpha('#0f1419', 0.8),
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: alpha('#00d4ff', 0.1),
          borderRadius: isTablet ? 1.5 : 2,
          overflow: 'hidden',
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto' }}>
          {realtimeAlerts.length === 0 ? (
            <Typography variant="body2" sx={{ color: alpha('#fff', 0.3) }}>
              Waiting for alerts...
            </Typography>
          ) : (
            <AnimatePresence>
              {realtimeAlerts.map((alert) => (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                >
                  <Chip
                    avatar={
                      <Avatar
                        sx={{
                          bgcolor: alpha(getAlertColor(alert.signal_type), 0.2),
                          width: 24,
                          height: 24,
                        }}
                      >
                        {getAlertIcon(alert.signal_type)}
                      </Avatar>
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {alert.strategy_id || 'Unknown'}
                        </Typography>
                        {alert.price && (
                          <Typography variant="caption" sx={{ color: '#00d4ff' }}>
                            ${alert.price.toFixed(2)}
                          </Typography>
                        )}
                      </Stack>
                    }
                    sx={{
                      bgcolor: alpha(getAlertColor(alert.signal_type), 0.1),
                      border: '1px solid',
                      borderColor: alpha(getAlertColor(alert.signal_type), 0.3),
                      color: '#fff',
                    }}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={responsiveSpacing} sx={{ mb: responsiveSpacing }}>
        {[
          { 
            label: 'Total Alerts', 
            value: stats.total, 
            color: '#00d4ff', 
            icon: <BellIcon />,
            trend: null,
          },
          { 
            label: 'Entry Signals', 
            value: stats.entries, 
            color: '#00ff88', 
            icon: <ArrowUpIcon />,
            trend: 'up',
          },
          { 
            label: 'Exit Signals', 
            value: stats.exits, 
            color: '#ff4757', 
            icon: <ArrowDownIcon />,
            trend: 'down',
          },
          { 
            label: 'Active Strategies', 
            value: stats.activeStrategies.size, 
            color: '#ffa502', 
            icon: <ChartIcon />,
            trend: null,
          },
        ].map((stat, index) => (
          <Grid item xs={6} md={3} key={index}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                sx={{
                  background: 'linear-gradient(135deg, ' + 
                    alpha(stat.color, 0.1) + ' 0%, ' + 
                    alpha(stat.color, 0.05) + ' 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid',
                  borderColor: alpha(stat.color, 0.3),
                  borderRadius: isTablet ? 1.5 : 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    background: `linear-gradient(90deg, transparent, ${stat.color}, transparent)`,
                    animation: 'shimmer 2s infinite',
                  }
                }}
              >
                <CardContent sx={{ p: isTablet ? 1.5 : 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: alpha('#fff', 0.6),
                          fontSize: isTablet ? '0.65rem' : '0.7rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        {stat.label}
                      </Typography>
                      <Stack direction="row" alignItems="baseline" spacing={1}>
                        <Typography 
                          variant={isTablet ? 'h6' : 'h4'}
                          sx={{ 
                            color: stat.color, 
                            fontWeight: 700,
                            lineHeight: 1,
                            mt: 0.5,
                          }}
                        >
                          {stat.value}
                        </Typography>
                        {stat.trend && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color: stat.trend === 'up' ? '#00ff88' : '#ff4757',
                            }}
                          >
                            {stat.trend === 'up' ? (
                              <ArrowUpIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <ArrowDownIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        )}
                      </Stack>
                    </Box>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: alpha(stat.color, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid',
                        borderColor: alpha(stat.color, 0.3),
                      }}
                    >
                      {React.cloneElement(stat.icon, { 
                        sx: { 
                          color: stat.color, 
                          fontSize: 24 
                        } 
                      })}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Main Content */}
      <Grid container spacing={responsiveSpacing}>
        {/* Alerts List */}
        <Grid item xs={12} lg={selectedStrategy ? 6 : 12}>
          <Paper
            elevation={0}
            sx={{
              background: alpha('#0f1419', 0.8),
              backdropFilter: 'blur(20px)',
              border: '1px solid',
              borderColor: alpha('#00d4ff', 0.1),
              borderRadius: isTablet ? 1.5 : 2,
              height: cardHeight,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Filter Bar */}
            <Box sx={{ p: isTablet ? 1.5 : 2, borderBottom: '1px solid', borderColor: alpha('#00d4ff', 0.1) }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search alerts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  fullWidth={isTablet}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: alpha('#000', 0.3),
                      '& fieldset': {
                        borderColor: alpha('#00d4ff', 0.2),
                      },
                      '&:hover fieldset': {
                        borderColor: alpha('#00d4ff', 0.4),
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#00d4ff',
                      },
                      '& input': {
                        color: '#fff',
                        fontSize: '0.875rem',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: <FilterIcon sx={{ color: alpha('#fff', 0.5), mr: 1 }} />,
                  }}
                />
                
                {/* Filter Chips */}
                <Stack direction="row" spacing={0.5} sx={{ overflowX: 'auto' }}>
                  {['all', 'entry', 'exit', 'early'].map(type => (
                    <Chip
                      key={type}
                      label={type.charAt(0).toUpperCase() + type.slice(1)}
                      onClick={() => setFilter(type)}
                      size={isTablet ? 'small' : 'medium'}
                      sx={{
                        bgcolor: filter === type ? alpha('#00d4ff', 0.2) : 'transparent',
                        color: filter === type ? '#00d4ff' : alpha('#fff', 0.5),
                        border: '1px solid',
                        borderColor: filter === type ? '#00d4ff' : alpha('#fff', 0.2),
                        '&:hover': {
                          bgcolor: alpha('#00d4ff', 0.1),
                        },
                        whiteSpace: 'nowrap',
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Box>

            {/* Alerts List */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: isTablet ? 1.5 : 2 }}>
              <AnimatePresence>
                {loading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}
                  >
                    <CircularProgress size={50} sx={{ color: '#00d4ff', mb: 2 }} />
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.4) }}>
                      Loading alerts...
                    </Typography>
                  </Box>
                ) : filteredAlerts.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}
                  >
                    <BellIcon sx={{ fontSize: isTablet ? 48 : 64, color: alpha('#fff', 0.1), mb: 2 }} />
                    <Typography variant="body1" sx={{ color: alpha('#fff', 0.3) }}>
                      Waiting for alerts...
                    </Typography>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.2) }}>
                      Signals will appear here when received
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {filteredAlerts.map((alert, index) => (
                      <motion.div
                        key={alert.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: Math.min(index * 0.05, 0.3) }}
                      >
                        <Card
                          onClick={() => {
                            if (alert.strategy_id) {
                              setSelectedStrategy(alert.strategy_id);
                            }
                          }}
                          sx={{
                            background: alpha(getAlertColor(alert.signal_type), 0.05),
                            border: '1px solid',
                            borderColor: alpha(getAlertColor(alert.signal_type), 0.2),
                            borderRadius: isTablet ? 1 : 1.5,
                            p: isTablet ? 1.5 : 2,
                            cursor: alert.strategy_id ? 'pointer' : 'default',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: alpha(getAlertColor(alert.signal_type), 0.4),
                              transform: 'translateX(4px)',
                              bgcolor: alpha(getAlertColor(alert.signal_type), 0.08),
                            }
                          }}
                        >
                          <Stack direction="row" alignItems="flex-start" spacing={isTablet ? 1 : 2}>
                            {/* Icon */}
                            <Avatar
                              sx={{
                                width: isTablet ? 32 : 36,
                                height: isTablet ? 32 : 36,
                                bgcolor: alpha(getAlertColor(alert.signal_type), 0.2),
                                border: '1px solid',
                                borderColor: alpha(getAlertColor(alert.signal_type), 0.3),
                              }}
                            >
                              {getAlertIcon(alert.signal_type)}
                            </Avatar>
                            
                            {/* Content */}
                            <Box flex={1}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, fontSize: isTablet ? '0.875rem' : '1rem' }}>
                                  {alert.strategy_id || 'System Alert'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: alpha('#fff', 0.4), fontSize: isTablet ? '0.7rem' : '0.75rem' }}>
                                  {new Date(alert.timestamp).toLocaleTimeString()}
                                </Typography>
                              </Stack>
                              
                              <Typography variant="caption" sx={{ color: alpha('#fff', 0.7), display: 'block', mb: 1, fontSize: isTablet ? '0.75rem' : '0.875rem' }}>
                                {alert.message}
                              </Typography>
                              
                              {/* Tags */}
                              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                {alert.signal && (
                                  <Chip
                                    label={alert.signal}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      bgcolor: alpha('#00d4ff', 0.1),
                                      color: '#00d4ff',
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                )}
                                {alert.price && (
                                  <Chip
                                    label={`$${alert.price.toFixed(2)}`}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      bgcolor: alpha('#ffa502', 0.1),
                                      color: '#ffa502',
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          </Stack>
                        </Card>
                      </motion.div>
                    ))}
                  </Stack>
                )}
              </AnimatePresence>
            </Box>
          </Paper>
        </Grid>

        {/* Strategy Progress Tracker */}
        {selectedStrategy && !isTablet && (
          <Grid item xs={12} lg={6}>
            <Box sx={{ position: 'relative' }}>
              <IconButton
                onClick={() => setSelectedStrategy(null)}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: 16,
                  zIndex: 1,
                  bgcolor: alpha('#000', 0.5),
                  color: '#fff',
                  '&:hover': {
                    bgcolor: alpha('#000', 0.7),
                  }
                }}
              >
                <ClearIcon />
              </IconButton>
              <StrategyProgressTracker
                strategyId={selectedStrategy}
                alerts={alerts.filter(a => a.strategy_id === selectedStrategy)}
              />
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Notification System */}
      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />

      {/* Animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          
          @keyframes bell-ring {
            0% { transform: rotate(0); }
            10% { transform: rotate(10deg); }
            20% { transform: rotate(-10deg); }
            30% { transform: rotate(10deg); }
            40% { transform: rotate(-10deg); }
            50% { transform: rotate(0); }
            100% { transform: rotate(0); }
          }
          
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `}
      </style>
    </Box>
  );
};

export default LiveAlertsTabEnhanced;
